import { apiClient } from "../apiClient";
import { CHANNELS } from "@common";
import type { MessageResponse } from "@interfaces/api.interface";
import type { Message, Conversation } from "@interfaces/chat.interface";

export interface ZaloOA {
  id: string;
  name: string;
}

export interface ZaloOAsResponse {
  code: number;
  data: {
    total: number;
    items: ZaloOA[];
  };
  message: string;
}

export type ZaloMessageResponse = MessageResponse;

export interface ZaloMessagesResponse {
  code: number;
  data: {
    total: number;
    offset: number;
    limit: number;
    items: ZaloMessageResponse[];
  };
  message: string;
}

export interface UploadZaloImageResponse {
  code: number;
  message: string;
  data?: {
    attachment_id: string;
  };
}

export interface SendZaloMessageParams {
  conversationId: string;
  type: "text" | "image";
  text?: string;
  attachmentId?: string;
  caption?: string;
}

export interface SendZaloMessageResponse {
  code: number;
  message: string;
  data?: {
    userId: string;
    messageId: string;
    attachmentId?: string;
    caption?: string;
  };
}

export const uploadZaloImage = async (file: File): Promise<string> => {
  const response = await apiClient.upload<UploadZaloImageResponse>(
    "/api/uploads/images",
    file
  );

  if (response.code !== 0 || !response.data?.attachment_id) {
    throw new Error(response.message || "Upload image failed");
  }

  return response.data.attachment_id;
};

export const sendZaloMessage = async (
  params: SendZaloMessageParams
): Promise<SendZaloMessageResponse> => {
  const { conversationId, type, text, attachmentId, caption } = params;
  const payload: Record<string, unknown> = { type };

  if (type === "text") {
    payload.text = text || "";
  } else {
    if (!attachmentId) {
      throw new Error("Missing attachmentId");
    }
    payload.attachmentId = attachmentId;
    if (caption) {
      payload.caption = caption;
    }
  }

  const response = await apiClient.post<SendZaloMessageResponse>(
    `/api/zalo/internal/conversations/${conversationId}/messages`,
    payload
  );

  if (response.code !== 0) {
    throw new Error(response.message || "Send message failed");
  }

  return response;
};

export const getZaloOAs = async (): Promise<ZaloOA[]> => {
  const response = await apiClient.get<ZaloOAsResponse>(
    "/api/zalo/internal/oas/all"
  );
  if (response.code === 0) {
    return response.data.items;
  }
  throw new Error(response.message || "Failed to fetch Zalo OAs");
};

export interface GetZaloMessagesByOAParams {
  oaId: string;
  offset?: number;
  limit?: number;
  signal?: AbortSignal;
}

export const getZaloMessagesByOA = async (
  params: GetZaloMessagesByOAParams
): Promise<ZaloMessagesResponse> => {
  const { oaId, offset = 0, limit = 20 } = params;
  const response = await apiClient.get<ZaloMessagesResponse>(
    "/api/zalo/internal/conversations/messages",
    {
      params: {
        oaId,
        offset,
        limit,
      },
      signal: params.signal,
    }
  );
  return response;
};

export interface GetZaloConversationMessagesParams {
  conversationId: string;
  offset?: number;
  limit?: number;
  signal?: AbortSignal;
}

export const getZaloConversationMessages = async (
  params: GetZaloConversationMessagesParams
): Promise<ZaloMessagesResponse> => {
  const { conversationId, offset = 0, limit = 25 } = params;
  const response = await apiClient.get<ZaloMessagesResponse>(
    `/api/zalo/internal/conversations/${conversationId}/messages/detail`,
    {
      params: {
        offset,
        limit,
      },
      signal: params.signal,
    }
  );
  return response;
};

export interface GetZaloMessagesParams {
  conversationId: string;
  offset?: number;
  limit?: number;
  signal?: AbortSignal;
}

export interface GetZaloMessagesDetailResult {
  messages: Message[];
  offset: number;
  total: number;
  limit: number;
}

export const getZaloMessagesDetailWithMeta = async (
  params: GetZaloMessagesParams
): Promise<GetZaloMessagesDetailResult> => {
  const { conversationId, offset = 0, limit = 100, signal } = params;

  try {
    const response = await getZaloConversationMessages({
      conversationId,
      offset,
      limit,
      signal,
    });

    const messages = response.data?.items || [];

    if (!Array.isArray(messages)) {
      console.error("Messages is not an array:", messages);
      return {
        messages: [],
        offset: response.data?.offset || 0,
        total: response.data?.total || 0,
        limit: response.data?.limit || 100,
      };
    }

    const transformedMessages = messages
      .map((msg, index) => {
        try {
          return transformZaloMessage(msg);
        } catch (error) {
          console.error(
            `Error transforming Zalo message at index ${index}:`,
            error,
            msg
          );
          return null;
        }
      })
      .filter((msg): msg is Message => msg !== null);

    return {
      messages: transformedMessages,
      offset: response.data?.offset || 0,
      total: response.data?.total || 0,
      limit: response.data?.limit || 100,
    };
  } catch (error) {
    console.error("Error in getZaloMessagesDetailWithMeta:", error);
    throw error;
  }
};

export const transformZaloMessage = (
  zaloMessage: ZaloMessageResponse
): Message => {
  const createdAt = new Date(zaloMessage.timestampMs).toISOString();

  const messageTypeMap: Record<string, Message["type"]> = {
    TEXT: "text",
    IMAGE: "image",
    VIDEO: "video",
    FILE: "file",
    LINK: "link",
    AUDIO: "audio",
    TEMPLATE: "text",
    LIST: "link",
    CALL_REQUEST: "text",
    STICKER: "image",
  };

  const type = messageTypeMap[zaloMessage.messageType] || "text";

  const isOutgoing =
    zaloMessage.direction === "OUTGOING" ||
    zaloMessage.direction === "OUTBOUND";
  const status: Message["status"] = isOutgoing ? "sent" : "read";

  const attachments =
    zaloMessage.attachments ||
    (zaloMessage.metadata?.attachments as Array<{
      type: string;
      payload?: { url?: string; id?: string };
      url?: string;
    }> | undefined);

  let stickerUrl: string | undefined;
  if (zaloMessage.messageType === "STICKER") {
    if (typeof zaloMessage.content === "string" && zaloMessage.content.startsWith("http")) {
      stickerUrl = zaloMessage.content;
    } else if (attachments && attachments.length > 0) {
      const stickerAttachment = attachments.find((att) => att.type === "sticker");
      if (stickerAttachment) {
        stickerUrl =
          (typeof stickerAttachment.payload?.url === "string" && stickerAttachment.payload?.url) ||
          (typeof stickerAttachment.url === "string" && stickerAttachment.url) ||
          (typeof zaloMessage.metadata?.linkUrl === "string" ? zaloMessage.metadata.linkUrl : undefined);
      }
    }
    if (!stickerUrl && typeof zaloMessage.metadata?.linkUrl === "string") {
      stickerUrl = zaloMessage.metadata.linkUrl;
    }
  }

  const body = zaloMessage.messageType === "STICKER" && stickerUrl
    ? stickerUrl
    : zaloMessage.content;

  return {
    id: zaloMessage.messageId || "",
    conversationId: zaloMessage.conversationId,
    sender: {
      id: zaloMessage.senderId,
      name: zaloMessage.senderName,
      avatar: zaloMessage.senderAvatar,
    },
    body,
    createdAt,
    status,
    type,
    meta: {
      receiverName: zaloMessage.receiverName,
      receiverAvatar: zaloMessage.receiverAvatar,
      receiverId: zaloMessage.receiverId,
      direction: isOutgoing ? "OUTBOUND" : "INBOUND",
      conversationName: zaloMessage.conversationName,
      conversationAvatarUrl:
        zaloMessage.conversationAvatarUrl || zaloMessage.receiverAvatar,
      provider: "zalo",
      originalMessageType: zaloMessage.messageType,
      ...(zaloMessage.actorSource && { actorSource: zaloMessage.actorSource }),
      ...(attachments && { attachments }),
      ...(zaloMessage.metadata && { ...zaloMessage.metadata }),
      ...(zaloMessage.messageType === "STICKER" && stickerUrl && { previewUrl: stickerUrl }),
    },
  };
};

export interface GetZaloConversationsResult {
  conversations: Conversation[];
  offset: number;
  total: number;
  limit: number;
}

export interface GetZaloConversationsParams {
  oaId: string;
  offset?: number;
  limit?: number;
  signal?: AbortSignal;
}

export const getZaloConversationsWithMeta = async (
  params: GetZaloConversationsParams
): Promise<GetZaloConversationsResult> => {
  const { oaId, offset = 0, limit = 50, signal } = params;

  try {
    const response = await getZaloMessagesByOA({
      oaId,
      offset,
      limit,
      signal,
    });

    const messages = response.data?.items || [];

    if (!Array.isArray(messages)) {
      console.error("Messages is not an array:", messages);
      return {
        conversations: [],
        offset: response.data?.offset || 0,
        total: response.data?.total || 0,
        limit: response.data?.limit || 50,
      };
    }

    const conversations: Conversation[] = [];
    const conversationMap = new Map<string, ZaloMessageResponse>();

    messages.forEach((msg) => {
      const existing = conversationMap.get(msg.conversationId);
      if (!existing || msg.timestampMs > existing.timestampMs) {
        conversationMap.set(msg.conversationId, msg);
      }
    });

    conversationMap.forEach((lastMessage, convId) => {
      const transformedMessage = transformZaloMessage(lastMessage);
      const conversationName: string =
        (transformedMessage.meta?.conversationName as string | undefined) ||
        (transformedMessage.meta?.receiverName as string | undefined) ||
        transformedMessage.sender.name ||
        "Unknown";
      const receiverAvatar: string =
        transformedMessage.meta?.conversationAvatarUrl ||
        transformedMessage.meta?.receiverAvatar ||
        transformedMessage.sender.avatar ||
        "";
      const isPinned = lastMessage.isPinned ?? false;

      const conversation: Conversation = {
        id: convId,
        channel: CHANNELS.ZALO,
        name: conversationName,
        fanpageName: undefined,
        avatar: receiverAvatar,
        isGroup: false,
        members: [],
        lastMessage: transformedMessage,
        unreadCount: 0,
        isPinned: isPinned,
        isMuted: false,
        typingUsers: [],
      };

      conversations.push(conversation);
    });

    return {
      conversations,
      offset: response.data?.offset || 0,
      total: response.data?.total || 0,
      limit: response.data?.limit || 50,
    };
  } catch (error) {
    console.error("Error in getZaloConversationsWithMeta:", error);
    throw error;
  }
};
